import React, { useState, useMemo } from 'lib/react';
import {
  EVButton,
  EVErrorOutline,
  EVGrid,
  EVList,
  EVTextField,
  EVTypography,
  EVSkeleton,
  EVGetAppOutlinedIcon,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import LinearProgressWithLabel from 'commonComponents/progress-bar';
import clsx from 'clsx';
import { base64ToArrayBuffer } from 'utils/app.utils';
import UploadBlueprintStyles from './UploadBlueprintStyles';

function UploadBlueprint({
  fileData,
  fileName,
  uploadError,
  onFileUpload,
  isUploading,
  isPageLoading,
}) {
  const { t } = useTranslation();
  const classes = UploadBlueprintStyles();

  const [fileDragged, setFileDragged] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  // #genai optional call keeps the original guard: click only fires on Enter when the target supports it
  const handleLabelKeyDown = (e) => e.key === 'Enter' && e.target.click?.();

  const handleFileUpload = (event) => {
    setUploadingFileName(event.target.files[0].name);
    onFileUpload(event);
  };
  const handleFileDrop = (event) => {
    event.preventDefault();
    setFileDragged(false);
    const { dataTransfer: { files: [file] } } = event;
    handleFileUpload({ target: { files: [file] } });
  };

  const fileDataURL = useMemo(() => {
    const byteArray = base64ToArrayBuffer(fileData.replace('data:application/pdf;base64,', ''));
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }, [fileData]);

  // #genai nested ternary extracted; branches stay in the original order (uploading, dragging, idle)
  const renderDropZoneContent = () => {
    if (isUploading) {
      return <LinearProgressWithLabel labels={[`Uploading ${uploadingFileName || fileName}`]} className={classes.linearProgressContainer} />;
    }

    if (fileDragged) {
      return (
        <>
          <EVTypography className={classes.uploadBlueprintLabelText}>
            {t('blueprint_order__drop_file')}
          </EVTypography>
          <EVButton
            variant="outlined"
            color="primary"
            component="label"
            loading={isUploading}
            htmlFor="blueprint-file-drag"
            style={{ width: '120px' }}
          >
            <EVGetAppOutlinedIcon />
          </EVButton>
        </>
      );
    }

    return (
      <>
        <EVTypography className={classes.uploadBlueprintLabelText}>
          {t('blueprint_order__drag_and_drop')}
          <br />
          {t('order__or')}
        </EVTypography>
        {uploadError && (
        <EVTypography className={classes.uploadBlueprintLabelErrorText}>
          <EVErrorOutline />
          {uploadError}
        </EVTypography>
        )}
        <EVButton
          variant="outlined"
          color="primary"
          component="label"
          loading={isUploading}
          htmlFor="blueprint-file-input"
          onKeyDown={handleLabelKeyDown}
        >
          {t('common__browse_file')}
        </EVButton>
      </>
    );
  };

  const renderFileInputLabel = () => {
    if (fileData && !isUploading) {
      return (
        <>
          <EVButton
            variant="outlined"
            color="primary"
            component="label"
            htmlFor="blueprint-file-input"
            className={classes.uploadDifferentFileButton}
            onKeyDown={handleLabelKeyDown}
          >
            {t('blueprint_order__upload_different_file')}
          </EVButton>
          <EVGrid container direction="column" wrap="nowrap" className={classes.uploadDifferentFileContainer}>
            <EVTypography className={classes.uploadBlueprintFileName}>
              {fileName}
            </EVTypography>
            <iframe
              src={fileDataURL}
              type="application/pdf"
              width="100%"
              className={classes.iFrameComponent}
              title={fileName}
            >
              {fileName}
            </iframe>
          </EVGrid>
        </>
      );
    }

    return (
      <EVGrid
        data-testid="upload-blueprint-drag-and-drop"
        className={clsx(classes.uploadBlueprintLabel, fileDragged && classes.uploadBlueprintLabelOnDrag)}
        onDragEnter={(e) => { e.preventDefault(); setFileDragged(true); }}
        onDragLeave={(e) => { e.preventDefault(); setFileDragged(false); }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleFileDrop}
      >
        {renderDropZoneContent()}
      </EVGrid>
    );
  };

  return (
    <>
      <EVGrid className={classes.uploadBlueprintHeader}>
        <EVTypography variant="h6" className={classes.uploadBlueprintInstructionsHeading}>{t('blueprint_order__instructions')}</EVTypography>
        <EVTypography>{t('blueprint_order__before_submit_data_points')}</EVTypography>
        <EVList className={classes.uploadBlueprintHeaderList}>
          {
            [
              t('blueprint_order__before_submit_data_point_1'),
              t('blueprint_order__before_submit_data_point_2'),
              t('blueprint_order__before_submit_data_point_3')]
              .map((text) => (<EVTypography key={text} component="li">{text}</EVTypography>))
          }
        </EVList>
      </EVGrid>
      <EVTextField
        type="file"
        id="blueprint-file-input"
        style={{ display: 'none' }}
        inputProps={{ accept: 'application/pdf' }}
        onChange={handleFileUpload}
        disabled={isUploading}
      />
      {isPageLoading ? <EVSkeleton style={{ height: '300px', borderRadius: '6px' }} animation="wave" variant="rect" />
        : renderFileInputLabel()}
    </>
  );
}

UploadBlueprint.defaultProps = {
  fileData: '',
  fileName: '',
  uploadError: '',
  isUploading: false,
  isPageLoading: false,
};

UploadBlueprint.propTypes = {
  fileData: PropTypes.string,
  fileName: PropTypes.string,
  uploadError: PropTypes.string,
  onFileUpload: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  isPageLoading: PropTypes.bool,
};

export default UploadBlueprint;
